// ============================================================================
// Section Style Builder — CSS from SectionConfig (M7)
// ============================================================================

import type { CSSProperties } from 'react';
import type { SectionConfig, OverlayConfig, LayoutConfig } from '../../editor/types';
import {
  CONTENT_MAX_WIDTH_PX,
  SECTION_SPACING_TOKEN_PX,
  ALIGN_X_CSS,
  ALIGN_Y_CSS,
  resolveSectionPaddingCSS,
} from '../../editor/constants/sectionDefaults';
import { resolveColorHex } from '../../editor/constants/colorTokens';

interface ResolvedImageEntry {
  image_id: number;
  data: Record<string, string>;
  alt: string;
  error?: string;
}

export function buildSectionStyle(
  config: SectionConfig | undefined,
  resolvedImages: Record<number, ResolvedImageEntry>,
): CSSProperties {
  if (!config) return {};
  const style: CSSProperties = {};

  // Background
  const bg = config.background;
  if (bg) {
    if (bg.type === 'color' && bg.color) {
      style.backgroundColor = bg.color;
    } else if (bg.type === 'image' && bg.image_id) {
      // Check resolved_data first (injected by backend resolve-path endpoint),
      // then fall back to editor-provided resolvedImages map.
      // resolved_data is a flat URL map {url, large, medium, ...} — not wrapped in {data: ...}
      const baked = (bg as unknown as Record<string, unknown>).resolved_data as Record<string, string> | undefined;
      const resolved = resolvedImages[bg.image_id];
      const url = baked?.url ?? baked?.large ?? resolved?.data?.url ?? resolved?.data?.large ?? '';
      if (url) {
        style.backgroundImage = `url(${url})`;
        style.backgroundSize = bg.image_size ?? 'cover';
        style.backgroundRepeat = 'no-repeat';
        // Focal point override takes precedence over image_position
        if (config.focal_point) {
          style.backgroundPosition = `${config.focal_point.x * 100}% ${config.focal_point.y * 100}%`;
        } else {
          style.backgroundPosition = bg.image_position ?? 'center center';
        }
      }
    } else if (bg.type === 'gradient' && bg.gradient?.trim()) {
      style.backgroundImage = bg.gradient;
    }
  }

  // Layout — note: maxWidth and padding are NOT on the outer section container.
  // They belong on the inner content wrapper via buildContentStyle() to avoid
  // clipping backgrounds/overlays.
  const layout = config.layout;
  if (layout) {
    if (layout.min_height !== 'auto') {
      style.minHeight = layout.min_height;
      // Flex is only needed when vertically centering content in a tall section (W3+W4)
      style.display = 'flex';
      style.flexDirection = 'column';
      style.alignItems = ALIGN_X_CSS[layout.content_align_x] ?? 'stretch';
      style.justifyContent = ALIGN_Y_CSS[layout.content_align_y] ?? 'flex-start';
    }
    // Text color: 'auto' = inherit, legacy 'light'/'dark', or color token ID
    if (layout.text_color && layout.text_color !== 'auto') {
      if (layout.text_color === 'light') {
        style.color = 'var(--chakra-colors-neutral-white)';
      } else if (layout.text_color === 'dark') {
        style.color = 'var(--chakra-colors-charcoal-charcoal)';
      } else {
        const hex = resolveColorHex(layout.text_color);
        if (hex) style.color = hex;
      }
    }
  }

  return style;
}

/**
 * Build CSS for the inner content wrapper (maxWidth + padding).
 * Apply to the <Box zIndex={1}> inside the section, NOT the outer container.
 * This prevents maxWidth/padding from clipping the background/overlay.
 */
export function buildContentStyle(
  config: SectionConfig | undefined,
  pageContentMaxWidth?: string,
): CSSProperties {
  if (!config) return {};
  const style: CSSProperties = {};
  const layout = config.layout;
  if (layout) {
    // Resolve 'inherit' → use page-level content_max_width; no implicit fallback
    const resolvedWidth = layout.content_max_width === 'inherit'
      ? (pageContentMaxWidth ?? 'full')
      : layout.content_max_width;
    style.maxWidth = CONTENT_MAX_WIDTH_PX[resolvedWidth] ?? '100%';
    style.padding = resolveSectionPaddingCSS(layout.padding);
    style.margin = '0 auto';
    style.width = '100%';

  }
  return style;
}

/**
 * Build a Chakra `sx` object with @media overrides for mobile layout.
 * Used on public pages (no previewMode) to apply responsive section styles.
 * Returns null when no mobile overrides exist.
 */
export function buildMobileContentSx(
  config: SectionConfig | undefined,
  pageContentMaxWidth?: string,
): Record<string, unknown> | null {
  const mobileOverrides = config?.responsive?.mobile;
  if (!mobileOverrides) return null;

  const desktopLayout = config?.layout;
  const merged = mergeResponsiveLayout(desktopLayout, mobileOverrides);
  if (!merged) return null;

  const overrides: Record<string, unknown> = {};

  // Compare padding
  const desktopPadding = resolveSectionPaddingCSS(desktopLayout?.padding);
  const mobilePadding = resolveSectionPaddingCSS(merged.padding);
  if (mobilePadding !== desktopPadding) {
    overrides.padding = mobilePadding;
  }

  // Compare maxWidth
  const resolveWidth = (layout: LayoutConfig | undefined) => {
    if (!layout) return '100%';
    const w = layout.content_max_width === 'inherit'
      ? (pageContentMaxWidth ?? 'full')
      : layout.content_max_width;
    return CONTENT_MAX_WIDTH_PX[w] ?? '100%';
  };
  const desktopMaxW = resolveWidth(desktopLayout);
  const mobileMaxW = resolveWidth(merged);
  if (mobileMaxW !== desktopMaxW) {
    overrides.maxWidth = mobileMaxW;
  }

  if (Object.keys(overrides).length === 0) return null;
  return { '@media (max-width: 768px)': overrides };
}

/**
 * Build a Chakra `sx` object with @media overrides for mobile flex container layout.
 * Returns null when no mobile overrides affect flex properties.
 */
export function buildMobileFlexSx(
  config: SectionConfig | undefined,
): Record<string, unknown> | null {
  const mobileOverrides = config?.responsive?.mobile;
  if (!mobileOverrides) return null;

  const desktopLayout = config?.layout;
  const merged = mergeResponsiveLayout(desktopLayout, mobileOverrides);
  if (!merged) return null;

  const overrides: Record<string, unknown> = {};

  // Compare flex direction
  const dDir = desktopLayout?.flex_direction;
  const mDir = merged.flex_direction;
  if (mDir !== dDir && mDir) overrides.flexDirection = mDir;

  // Compare gap
  const dGap = desktopLayout?.gap;
  const mGap = merged.gap;
  if (mGap !== dGap && mGap) {
    const gapPx = SECTION_SPACING_TOKEN_PX[mGap];
    if (gapPx) overrides.gap = gapPx;
  } else if (mGap !== dGap && !mGap) {
    // Mobile explicitly unsets gap
    overrides.gap = '0';
  }

  // Compare justify/align
  const dJC = desktopLayout?.justify_content;
  const mJC = merged.justify_content;
  if (mJC !== dJC && mJC) overrides.justifyContent = mJC;

  const dAI = desktopLayout?.align_items;
  const mAI = merged.align_items;
  if (mAI !== dAI && mAI) overrides.alignItems = mAI;

  if (Object.keys(overrides).length === 0) return null;
  return { '@media (max-width: 768px)': overrides };
}

/**
 * Build CSS for the block container inside a section (flex layout from Advanced Layout settings).
 * Apply to the inner wrapper that holds the block list, NOT the content wrapper (which handles maxWidth/padding).
 */
export function buildFlexContainerStyle(
  config: SectionConfig | undefined,
): CSSProperties {
  const layout = config?.layout;
  const style: CSSProperties = {
    display: 'flex',
    flexDirection: layout?.flex_direction ?? 'column', // blocks stack vertically by default (CSS flex default is row)
  };
  if (layout?.flex_wrap) style.flexWrap = layout.flex_wrap;
  if (layout?.justify_content) style.justifyContent = layout.justify_content;
  if (layout?.align_items) style.alignItems = layout.align_items;
  // Gap: only apply if explicitly configured — no implicit spacing
  const effectiveGap = layout?.gap;
  if (effectiveGap) {
    const gapPx = SECTION_SPACING_TOKEN_PX[effectiveGap];
    if (gapPx) style.gap = gapPx;
  }
  return style;
}

/**
 * Merge mobile overrides over desktop layout config.
 * Used in editor preview (device === 'mobile') to compute effective layout.
 */
export function mergeResponsiveLayout(
  layout: LayoutConfig | undefined,
  mobileOverrides: Partial<LayoutConfig> | undefined,
): LayoutConfig | undefined {
  if (!mobileOverrides || Object.keys(mobileOverrides).length === 0) return layout;
  if (!layout) return mobileOverrides as LayoutConfig;
  return { ...layout, ...mobileOverrides };
}

export function buildOverlayStyle(overlay: OverlayConfig | undefined): CSSProperties {
  if (!overlay || overlay.type === 'none') return {};
  const opacity = overlay.opacity / 100; // CRITICAL: stored 0–100, CSS needs 0–1

  if (overlay.type === 'dark') {
    return { backgroundColor: `rgba(0, 0, 0, ${opacity})` };
  }
  if (overlay.type === 'light') {
    return { backgroundColor: `rgba(255, 255, 255, ${opacity})` };
  }
  // W2: For color overlay, bake alpha into rgba instead of using CSS opacity.
  // This avoids double-application if overlay.color is already rgba, and ensures
  // consistency with dark/light presets.
  if (overlay.type === 'color' && overlay.color) {
    const hex = overlay.color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return { backgroundColor: `rgba(${r}, ${g}, ${b}, ${opacity})` };
  }
  // For gradient overlays: new data sets opacity=100 with alpha baked into
  // gradient stops. Old data may have opacity<100 — preserve backward compat.
  if (overlay.type === 'gradient' && overlay.gradient?.trim()) {
    if (overlay.opacity >= 100 || overlay.opacity === undefined) {
      return { backgroundImage: overlay.gradient };
    }
    return { backgroundImage: overlay.gradient, opacity };
  }
  return {};
}
